import { provide, inject } from "vue";
import type { InjectionKey, Ref } from "vue";
import { assign, createMachine, raise } from "xstate-4";
import type { InterpreterFrom } from "xstate-4";
import { useInterpret, useActor } from "@xstate-4/vue";
import { xlog } from "../../utils/xstate";
import axios from "vue-app/utils/axios";
import { getUnixEndOfDay, getUnixStartOfDayPlusOneSecond } from "vue-app/datesDatepicker/datesDatepicker";
import { machineActivityPreview } from "../../components/Customer/machines/machineCustomerPreviewActivityPreview";
import { dayjsWithTz, getUnix } from "vue-app/utils/functions";
import { gtagSend } from "vue-app/plugins/edroneGtag";
import { Dayjs } from "dayjs";
import { datesDatepickerShortcuts } from "vue-app/datesDatepicker/datesDatepickerShortcuts";
import { AxiosError } from "axios";

export type Customer = {
  customerId: number;
  userId: number;
  creationDate: number;
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  language: string;
  country: string;
  city: string;
  birthday: string;
  genderType: string;
  subscription: {
    emailStatus: string;
    emailStatusModificationDate: number | Dayjs;
    smsStatus: string;
    smsStatusModificationDate: number | Dayjs;
    whatsappStatus: string;
    whatsappStatusModificationDate: number | Dayjs;
  };
  purchasing: Purchase;
  system: {
    ip: string;
    smsSubscriptionStatusReason: string;
    whatsappSubscriptionStatusReason: string;
    subscriptionStatusReason: string;
  };
  tags: string[];
  deleted: boolean;
  exist: boolean;
};

export type Purchase = {
  activitySegment: string;
  aov: number;
  lastOrderValue: number;
  lastVisitDate: number;
  ordersCount: number;
  purchaseFrequencySegment: string;
  purchaseValueSegment: string;
  totalOrdersValue: number;
  visitsCount: number;
};

type Form = {
  birthday: Date | null;
  changed: boolean;
  city: string | null;
  country: string | null;
  firstName: string | null;
  genderType: string | null;
  language: string;
  lastName: string | null;
  phone: string | null;
  subscriptionEmailStatus: string;
  subscriptionSMSStatus: string;
  subscriptionWhatsappStatus: string;
  tags: string[];
};

export type Activity = {
  date: number;
  inboxMessageData: null | {
    inboxConversationMessageType: string | null;
    message: string;
  };
  emailEventData: null | {
    engagementMessageId: number;
    subject: string;
    engagementType: string | null;
    campaignName: string;
  };
  whatsappEventData: null | {
    campaignName: string | null;
    engagementMessageId: number;
    engagementType: string | null;
  };
  smsEventData: null | {
    campaignName: string | null;
    engagementMessageId: number;
    engagementType: string | null;
  };
  orderData: null | {
    orderId: string;
    orderCurrency: string;
    engagementAttributionReasons: string[] | null;
    customerOrderId: number;
    edroneImpacted: boolean;
    basePaymentValue: number;
  };
  type: ActivityFiltersType;
  automationReceivedData: null | {
    customFlowId: number | null;
    customerEngagementStatus: string | null;
    subject: string;
    engagementType: string | null;
    notDisplayedReasons: object | null;
    receivedEmail: boolean;
    customerEngagementId: number;
    engagementMessageId: number;
    customFlowName: string | null;
    requestLocalRejectReason: string | null;
    engagementId: number;
    campaignName: string;
    customFlowElementId: string | null;
    channelType: string | null;
  };
  products: Product[] | null;
  subscriptionStatusChangeData: null | {
    subscriptionStatusReasonBefore: string | null;
    subscriptionStatusAfter: "UNSUBSCRIBED" | "UNKNOWN" | "SUBSCRIBED" | null;
    subscriptionStatusBefore: "UNSUBSCRIBED" | "UNKNOWN" | "SUBSCRIBED" | null;
    subscriptionStatusReasonAfter: string | null;
  };
};

export type ActivityFiltersType =
  | "PRODUCT_VIEW"
  | "ADD_TO_CART"
  | "ORDER"
  | "EMAIL_OPEN"
  | "EMAIL_CLICK"
  | "EMAIL_AUTOMATION_RECEIVED"
  | "SUBSCRIPTION_CHANGE"
  | "INBOX_MESSAGE"
  | "SMS_DELIVERED"
  | "SMS_CLICK"
  | "WHATSAPP_DELIVERED"
  | "WHATSAPP_OPEN"
  | "WHATSAPP_CLICK";

type Tag = {
  tagId: string;
  tagName: string;
  isDeleted?: boolean;
};

type Product = {
  trackerProductId: number;
  image: string | null;
  amount: number;
  productId: string;
  title: string;
  url: string;
  price: number;
  description?: string;
};

export type Context = {
  customer: Customer | null;
  activities: Activity[] | null;
  form: Form | null;
  tags: Tag[];
  selectedTab: "activities" | "properties" | "tags" | "purchasing" | "system" | null;
  customerId: number | string | null;
  fetchingActivitiesError: boolean;
  activityFilters: string[];
  activityRange: [Date, Date];
  activitySearch: string;
  availableActivityFilters: { name: string; value: string }[];
  confirmationModalType: "unsubscribe" | "delete" | null;
};

type Services = {
  fetchCustomer: { data: Customer };
  updateCustomer: { data: Customer };
  fetchActivities: { data: Activity[] };
  fetchTags: { data: Tag[] };
  createTag: { data: Tag };
  deleteCustomer: { data: null };
  exportToHTML: { data: null };
  clearTPCID: { data: null };
};

export const machineCustomerPreview = createMachine(
  {
    id: "CustomerPreview",
    tsTypes: {} as import("./machineCustomerPreview.typegen").Typegen0,
    context: {
      customer: null,
      activities: null,
      form: null,
      selectedTab: null,
      tags: [],
      customerId: null,
      fetchingActivitiesError: false,
      activityFilters: [],
      activityRange: [datesDatepickerShortcuts[2].onClick()[0], datesDatepickerShortcuts[0].onClick()[1]],
      activitySearch: "",
      availableActivityFilters: [
        { name: window._trans._inbox.customer.activity.filters.PRODUCT_VIEW, value: "1" },
        { name: window._trans._inbox.customer.activity.filters.ADD_TO_CART, value: "2" },
        { name: window._trans._inbox.customer.activity.filters.ORDER, value: "4" },
        { name: window._trans._inbox.customer.activity.filters.EMAIL_OPEN, value: "8" },
        { name: window._trans._inbox.customer.activity.filters.EMAIL_CLICK, value: "16" },
        { name: window._trans._inbox.customer.activity.filters.EMAIL_AUTOMATION_RECEIVED, value: "32" },
        { name: window._trans._inbox.customer.activity.filters.SUBSCRIPTION_CHANGE, value: "64" },
        { name: window._trans._inbox.customer.activity.filters.INBOX_MESSAGE, value: "128" },
        { name: window._trans._inbox.customer.activity.filters.SMS_DELIVERED, value: "256" },
        { name: window._trans._inbox.customer.activity.filters.SMS_CLICK, value: "512" },
        { name: window._trans._inbox.customer.activity.filters.WHATSAPP_DELIVERED, value: "1024" },
        { name: window._trans._inbox.customer.activity.filters.WHATSAPP_OPEN, value: "2048" },
        { name: window._trans._inbox.customer.activity.filters.WHATSAPP_CLICK, value: "4096" },
      ],
      confirmationModalType: null,
    },
    initial: "closed",
    states: {
      closed: {
        tags: ["closed"],
        on: {
          OPEN: {
            target: "loadingCustomer",
            actions: ["assignCustomerId", "updateStatusInLocalStorage", "gtag"],
          },
        },
      },
      idle: {
        tags: "idle",
        entry: ["assignTab"],
        on: {
          CLOSE: {
            target: "closed",
            actions: ["clearContextData", "updateStatusInLocalStorage"],
          },
          SELECT_TAB: {
            actions: ["assignTab", "gtag"],
          },
          FETCH_ACTIVITIES: {
            target: "pending.fetchingActivities",
            actions: ["gtag"],
          },
          UPDATE_FORM: {
            actions: ["updateForm"],
          },
          ADD_TAG: {
            actions: ["updateForm"],
          },
          CREATE_TAG: [
            {
              actions: ["showIsDemoToast"],
              cond: "isDemo",
            },
            {
              target: "pending.creatingTag",
            },
          ],
          REMOVE_TAG: {
            actions: ["updateForm"],
          },
          OPEN_CONFIRMATION_MODAL: {
            target: "confirmationModal",
            actions: ["assignConfirmationModalType"],
          },
          SAVE: [
            {
              actions: ["showIsDemoToast"],
              cond: "isDemo",
            },
            {
              target: "pending.savingCustomer",
              actions: ["gtag"],
              cond: "isFormChanged",
            },
          ],
          CLEAR_TPCID: [
            {
              actions: ["showIsDemoToast"],
              cond: "isDemo",
            },
            {
              target: "pending.clearingTPCID",
              actions: ["gtag"],
            },
          ],
          EXPORT_TO_HTML: [
            {
              actions: ["showIsDemoToast"],
              cond: "isDemo",
            },
            {
              target: "pending.exportingToHTML",
              actions: ["gtag"],
            },
          ],
          OPEN_ACTIVITY_PREVIEW: {
            target: "watchingActivityPreview",
            actions: ["gtag"],
          },
          UPDATE_ACTIVITY_FILTERS: {
            actions: ["assignActivityFilters"],
          },
          UPDATE_ACTIVITY_SEARCH: {
            actions: ["assignActivitySearch"],
          },
          UPDATE_ACTIVITY_RANGE: {
            actions: ["assignActivityRange"],
          },
          CLEAR_ACTIVITY_FILTERS_AND_SEARCH: {
            actions: ["assignActivityFilters", "assignActivitySearch"],
          },
        },
      },
      loadingCustomer: {
        tags: "loadingCustomer",
        invoke: [
          {
            src: "fetchCustomer",
            id: "fetchCustomer",
            onDone: [
              {
                actions: ["assignCustomer"],
                target: "emptyCustomer",
                cond: "isCustomerEmpty",
              },
              {
                // reset activities after fetching customer
                actions: ["assignCustomer", "assignActivities"],
                target: "idle",
                cond: (context) => context.activities !== null,
              },
              {
                actions: ["assignCustomer"],
                target: "idle",
              },
            ],
            onError: [
              {
                target: "errorGettingCustomer",
              },
            ],
          },
          {
            src: "fetchTags", // when tags are ready, they will be assigned by send action from service (also error handled there)
            id: "fetchTags",
          },
        ],
      },
      pending: {
        tags: "pending",
        states: {
          savingCustomer: {
            tags: "savingCustomer",
            invoke: {
              src: "updateCustomer",
              id: "updateCustomer",
              onDone: [
                {
                  target: "#CustomerPreview.idle",
                  actions: ["assignCustomer", "showSuccessSavingToast"],
                },
              ],
              onError: [
                {
                  target: "#CustomerPreview.idle",
                  actions: ["showErrorSavingToast"],
                },
              ],
            },
          },
          deletingCustomer: {
            tags: "deletingCustomer",
            invoke: {
              src: "deleteCustomer",
              id: "deleteCustomer",
              onDone: [
                {
                  target: "#CustomerPreview.emptyCustomer",
                  actions: ["showSuccessDeletingCustomerToast"],
                },
              ],
              onError: [
                {
                  target: "#CustomerPreview.idle",
                  actions: ["showErrorDeletingCustomerToast"],
                },
              ],
            },
          },
          unsubscribingAll: {
            tags: "unsubscribingAll",
            invoke: {
              src: "unsubscribeAll",
              id: "unsubscribeAll",
              onDone: [
                {
                  target: "#CustomerPreview.idle",
                  actions: ["updateCustomerSubscriptions", "showSuccessUnsubscribeAllToast"],
                },
              ],
              onError: [
                {
                  target: "#CustomerPreview.idle",
                  actions: ["showErrorUnsubscribeAllToast"],
                },
              ],
            },
          },
          creatingTag: {
            tags: "creatingTag",
            // @ts-ignore
            invoke: {
              src: "createTag",
              id: "createTag",
              onDone: [
                {
                  target: "#CustomerPreview.idle",
                  actions: [
                    "assignTags",
                    raise((_ctx, event) => {
                      return { type: "ADD_TAG", tag: event.data.tagId };
                    }),
                    "showSuccessCreatingTagToast",
                  ],
                },
              ],
              onError: [
                {
                  target: "#CustomerPreview.idle",
                  actions: ["showErrorCreatingTagToast"],
                },
              ],
            },
          },
          fetchingActivities: {
            tags: "fetchingActivities",
            entry: [assign({ fetchingActivitiesError: false })],
            invoke: {
              src: "fetchActivities",
              id: "fetchActivities",
              onDone: [
                {
                  target: "#CustomerPreview.idle",
                  actions: ["assignActivities"],
                },
              ],
              onError: [
                {
                  target: "#CustomerPreview.idle",
                  actions: [assign({ fetchingActivitiesError: true }), "assignActivities"],
                },
              ],
            },
          },
          clearingTPCID: {
            tags: "clearingTPCID",
            invoke: {
              src: "clearTPCID",
              id: "clearTPCID",
              onDone: [
                {
                  target: "#CustomerPreview.idle",
                  actions: ["showSuccessClearingTPCIDToast"],
                },
              ],
              onError: [
                {
                  target: "#CustomerPreview.idle",
                  actions: ["showErrorClearingTPCIDToast"],
                },
              ],
            },
          },
          exportingToHTML: {
            tags: "exportingToHTML",
            invoke: {
              src: "exportToHTML",
              id: "exportToHTML",
              onDone: [
                {
                  target: "#CustomerPreview.idle",
                  actions: ["showSuccessExportingToHTMLToast"],
                },
              ],
              onError: [
                {
                  target: "#CustomerPreview.idle",
                  actions: ["showErrorExportingToHTMLToast"],
                },
              ],
            },
          },
        },
      },
      errorGettingCustomer: {
        tags: "errorGettingCustomer",
        on: {
          CLOSE: {
            target: "closed",
            actions: ["clearContextData", "updateStatusInLocalStorage"],
          },
          RETRY_CUSTOMER: {
            target: "loadingCustomer",
          },
        },
      },
      emptyCustomer: {
        tags: "emptyCustomer",
        description: "Deleted or not exist customer view",
        on: {
          CLOSE: {
            target: "closed",
            actions: ["clearContextData", "updateStatusInLocalStorage"],
          },
        },
      },
      watchingActivityPreview: {
        tags: ["watchingActivityPreview"],
        invoke: {
          src: "activityPreviewModal",
          id: "activityPreviewModal",
          data: {
            details: (context: Context, event: { type: "OPEN_ACTIVITY_PREVIEW"; details: Activity }) => event.details,
            email: null,
            pages: 1,
            currentPage: 1,
            type: (context: Context, event: { type: "OPEN_ACTIVITY_PREVIEW"; details: Activity }) =>
              event.details.products?.length ? "product" : event.details.emailEventData ? "email" : null,
          },
          onDone: [
            {
              target: "idle",
            },
          ],
        },
      },
      confirmationModal: {
        tags: ["confirmationModal"],
        on: {
          CLOSE_CONFIRMATION_MODAL: {
            target: "idle",
            actions: ["assignConfirmationModalType"],
          },
          DELETE_CUSTOMER: [
            {
              actions: ["showIsDemoToast"],
              cond: "isDemo",
            },
            {
              target: "#CustomerPreview.pending.deletingCustomer",
              actions: ["gtag"],
            },
          ],
          UNSUBSCRIBE_ALL: [
            {
              actions: ["showIsDemoToast"],
              cond: "isDemo",
            },
            {
              target: "#CustomerPreview.pending.unsubscribingAll",
              actions: ["gtag"],
            },
          ],
        },
      },
    },
    on: {
      ASSIGN_TAGS: {
        actions: ["assignTags"],
      },
      UPDATE_CUSTOMER: {
        target: "loadingCustomer",
        actions: ["assignCustomerId"],
      },
    },
    schema: {
      events: {} as
        | { type: "OPEN"; customerId: number | string }
        | { type: "UPDATE_CUSTOMER"; customerId: number | string }
        | { type: "CLOSE" }
        | { type: "SELECT_TAB"; tab: "activities" | "properties" | "tags" | "system" | null }
        | { type: "CLEAR_TPCID" }
        | { type: "EXPORT_TO_HTML" }
        | { type: "UNSUBSCRIBE_ALL" }
        | { type: "DELETE_CUSTOMER" }
        | { type: "FETCH_ACTIVITIES"; filters: string[]; range: Date[]; search: string }
        | { type: "SAVE" }
        | { type: "RETRY_CUSTOMER" }
        | { type: "UPDATE_FORM"; property: string; value: string }
        | { type: "ADD_TAG"; tag: string }
        | { type: "CREATE_TAG"; tag: string }
        | { type: "REMOVE_TAG"; tag: string }
        | { type: "ASSIGN_TAGS"; data: Tag[] }
        | { type: "UPDATE_ACTIVITY_FILTERS"; data: string }
        | { type: "UPDATE_ACTIVITY_SEARCH"; data: string }
        | { type: "UPDATE_ACTIVITY_RANGE"; data: [Date, Date] }
        | { type: "CLEAR_ACTIVITY_FILTERS_AND_SEARCH" }
        | { type: "OPEN_ACTIVITY_PREVIEW"; details: Activity }
        | { type: "OPEN_CONFIRMATION_MODAL"; confirmation_type: "unsubscribe" | "delete" }
        | { type: "CLOSE_CONFIRMATION_MODAL" },
      context: {} as Context,
      services: {} as Services,
    },
    predictableActionArguments: true,
    preserveActionOrder: true,
  },
  {
    actions: {
      assignConfirmationModalType: assign({
        confirmationModalType: (context, event) => {
          xlog({ label: "assignConfirmationModalType", type: "action", context, event });
          return event.type === "CLOSE_CONFIRMATION_MODAL" ? null : event.confirmation_type;
        },
      }),
      assignCustomerId: assign({
        customerId: (context, event) => {
          xlog({ label: "assignCustomerId", type: "action", context, event });
          return event.customerId;
        },
      }),
      assignTab: assign({
        selectedTab: (context, event) => {
          xlog({ label: "assignTab", type: "action", context, event });
          if (event.type === "SELECT_TAB") {
            return event.tab;
          }
          return context.selectedTab || "properties";
        },
      }),
      assignCustomer: assign({
        customer: (context, event) => {
          xlog({ label: "assignCustomer", type: "action", context, event });
          return event.data;
        },
        form: (context, event) => {
          xlog({ label: "assignForm", type: "action", context, event });
          return {
            firstName: event.data?.firstName || null,
            lastName: event.data?.lastName || null,
            phone: event.data?.phone || null,
            language: event.data?.language || "en",
            country: event.data?.country || null,
            city: event.data?.city || null,
            birthday: event.data?.birthday ? dayjsWithTz(event.data?.birthday).toDate() : null,
            genderType: event.data?.genderType || "UNKNOWN",
            subscriptionEmailStatus: event.data?.subscription?.emailStatus || "UNKNOWN",
            subscriptionSMSStatus: event.data?.subscription?.smsStatus || "UNKNOWN",
            subscriptionWhatsappStatus: event.data?.subscription?.whatsappStatus || "UNKNOWN",
            tags: event.data?.tags || [],
            changed: false,
          };
        },
      }),
      updateCustomerSubscriptions: assign({
        customer: (context, event) => {
          xlog({ label: "updateCustomerSubscriptions", type: "action", context, event });
          return {
            ...context.customer,
            subscription: {
              ...context.customer?.subscription,
              emailStatus: "UNSUBSCRIBED",
              smsStatus: "UNSUBSCRIBED",
              whatsappStatus: "UNSUBSCRIBED",
              emailStatusModificationDate: dayjsWithTz(),
              smsStatusModificationDate: dayjsWithTz(),
              whatsappStatusModificationDate: dayjsWithTz(),
            },
          } as Customer;
        },
        form: (context, event) => {
          xlog({ label: "updateCustomerSubscriptions", type: "action", context, event });
          return {
            ...context.form,
            subscriptionEmailStatus: "UNSUBSCRIBED",
            subscriptionSMSStatus: "UNSUBSCRIBED",
            subscriptionWhatsappStatus: "UNSUBSCRIBED",
          } as Form;
        },
      }),
      assignActivities: assign({
        activities: (context, event) => {
          xlog({ label: "assignActivities", type: "action", context, event });
          if (event.type === "done.invoke.fetchCustomer") {
            return null;
          }
          if (event.type === "error.platform.fetchActivities") {
            return [] as Activity[];
          }
          return (
            event.data.length ? event.data.sort((a: Activity, b: Activity) => b.date - a.date) : []
          ) as Activity[];
        },
      }),
      assignActivityFilters: assign({
        activityFilters: (context, event) => {
          xlog({ label: "assignActivityFilters", type: "action", context, event });
          if (event.type === "CLEAR_ACTIVITY_FILTERS_AND_SEARCH") {
            return [];
          }
          if (context.activityFilters.includes(event.data)) {
            return context.activityFilters.filter((item) => item !== event.data);
          } else {
            return [...context.activityFilters, event.data];
          }
        },
      }),
      assignActivitySearch: assign({
        activitySearch: (context, event) => {
          xlog({ label: "assignActivitySearch", type: "action", context, event });
          if (event.type === "CLEAR_ACTIVITY_FILTERS_AND_SEARCH") {
            return "";
          }
          return event.data || "";
        },
      }),
      assignActivityRange: assign({
        activityRange: (context, event) => {
          xlog({ label: "assignActivityRange", type: "action", context, event });
          return event.data;
        },
      }),
      assignTags: assign({
        tags: (context, event) => {
          xlog({ label: "assignTags", type: "action", context, event });
          if (event.type === "done.invoke.createTag") {
            return [...context.tags, { tagName: event.data.tagName, tagId: event.data.tagId }] as Tag[];
          }
          return event.data
            .filter((tag: Tag) => !tag.isDeleted)
            .map((tag: Tag) => {
              return { tagName: tag.tagName, tagId: tag.tagId };
            }) as Tag[];
        },
      }),
      clearContextData: assign({
        customer: null,
        activities: null,
        form: null,
        selectedTab: null,
        tags: [],
        customerId: null,
      }),
      updateStatusInLocalStorage: (context, event) => {
        xlog({ label: "updateStatusInLocalStorage", type: "action" });
        if (event.type === "CLOSE") {
          localStorage.setItem("customer-preview-is-open", "false");
        }
        if (event.type === "OPEN") {
          localStorage.setItem("customer-preview-is-open", "true");
        }
      },
      updateForm: assign({
        form: (context, event) => {
          xlog({ label: "updateForm", type: "action", context, event });
          if (!context.form) {
            return null;
          }
          if (event.type === "ADD_TAG") {
            return {
              ...context.form,
              // eslint-disable-next-line no-unsafe-optional-chaining
              tags: [...context.form?.tags, event.tag],
              changed: true,
            };
          }
          if (event.type === "REMOVE_TAG") {
            return {
              ...context.form,
              // eslint-disable-next-line no-unsafe-optional-chaining
              tags: [...context.form?.tags.filter((tag: string) => tag !== event.tag)],
              changed: true,
            };
          }
          return {
            ...context.form,
            [event.property]: event.value,
            changed: true,
          };
        },
      }),
      showSuccessSavingToast: () => {
        window.showToast(window._trans._inbox.customer.notifications.saveSuccess, "success");
      },
      showErrorSavingToast: (context, event) => {
        if (event.data instanceof AxiosError) {
          if (event.data?.response?.data?.message.includes("Phone number already exists for another customer:")) {
            window.showToast(window._trans._inbox.customer.notifications.saveErrorDuplicatePhoneNumber, "error");
            return;
          }
        }
        window.showToast(window._trans._inbox.customer.notifications.saveError, "error");
      },
      showSuccessCreatingTagToast: () => {
        window.showToast(window._trans._inbox.customer.notifications.createTagSuccess, "success");
      },
      showErrorCreatingTagToast: () => {
        window.showToast(window._trans._inbox.customer.notifications.createTagError, "error");
      },
      showSuccessDeletingCustomerToast: () => {
        window.showToast(window._trans._inbox.customer.notifications.deleteSuccess, "success");
      },
      showErrorDeletingCustomerToast: () => {
        window.showToast(window._trans._inbox.customer.notifications.deleteError, "error");
      },
      showSuccessUnsubscribeAllToast: () => {
        window.showToast("Unsubscribed customer from all channels", "success");
      },
      showErrorUnsubscribeAllToast: () => {
        window.showToast("Error while trying to unsubscribed customer from all channels", "error");
      },
      showSuccessClearingTPCIDToast: () => {
        window.showToast(window._trans._inbox.customer.notifications.clearSuccess, "success");
      },
      showErrorClearingTPCIDToast: () => {
        window.showToast(window._trans._inbox.customer.notifications.clearError, "error");
      },
      showSuccessExportingToHTMLToast: () => {
        window.showToast(window._trans._inbox.customer.notifications.exportSuccess, "success");
        window.open("/app/export", "_blank");
      },
      showErrorExportingToHTMLToast: () => {
        window.showToast(window._trans._inbox.customer.notifications.exportError, "error");
      },
      showIsDemoToast: (context, event) => {
        xlog({ label: "showIsDemoToast", type: "action", context, event });
        window.showToast(window._trans._inbox.inboxDemo.disabled, "info");
      },
      gtag: (context, event) => {
        xlog({ label: "gtag", type: "action", context, event });
        switch (event.type) {
          case "OPEN":
            gtagSend({
              event: "button_click",
              component_type: "secondary",
              component_variant: "default",
              component_icon_name: "no_icon",
              component_state: "active",
              component_label: "edrone",
              functionality_group: "inbox",
              functionality: "inbox",
              view: "chat",
              text: "customer_preview",
            });
            return;
          case "SELECT_TAB":
            gtagSend({
              event: "navigation_tab",
              component_icon_name: "no_icon",
              component_state: "active",
              component_label: "edrone",
              functionality_group: "inbox",
              functionality: "inbox",
              view: "customer_preview",
              text: `${event.tab}`,
            });
            return;
          case "CLEAR_TPCID":
            gtagSend({
              event: "button_icon_click",
              component_icon_name: "broom-wide",
              component_state: "active",
              component_label: "edrone",
              functionality_group: "inbox",
              functionality: "inbox",
              view: "customer_preview",
              text: "clear_tpcid",
            });
            return;
          case "EXPORT_TO_HTML":
            gtagSend({
              event: "button_icon_click",
              component_icon_name: "download",
              component_state: "active",
              component_label: "edrone",
              functionality_group: "inbox",
              functionality: "inbox",
              view: "customer_preview",
              text: "export_to_html",
            });
            return;
          case "UNSUBSCRIBE_ALL":
            gtagSend({
              event: "button_icon_click",
              component_icon_name: "user-xmark",
              component_state: "active",
              component_label: "edrone",
              functionality_group: "inbox",
              functionality: "inbox",
              view: "customer_preview",
              text: "unsubscribe_all",
            });
            return;
          case "DELETE_CUSTOMER":
            gtagSend({
              event: "button_icon_click",
              component_icon_name: "trash_can",
              component_state: "active",
              component_label: "edrone",
              functionality_group: "inbox",
              functionality: "inbox",
              view: "customer_preview",
              text: "delete_customer",
            });
            return;
          case "SAVE":
            gtagSend({
              event: "button_click",
              component_type: "primary",
              component_variant: "default",
              component_icon_name: "no_icon",
              component_state: "active",
              component_label: "edrone",
              functionality_group: "inbox",
              functionality: "inbox",
              view: "customer_preview",
              text: "save",
            });
            return;
          case "OPEN_ACTIVITY_PREVIEW":
            gtagSend({
              event: "select",
              component_icon_name: "no_icon",
              component_state: "active",
              component_label: "edrone",
              functionality_group: "inbox",
              functionality: "inbox",
              view: "customer_preview",
              text: "open_activity_preview",
            });
            return;
          case "FETCH_ACTIVITIES":
            gtagSend({
              event: "select",
              component_icon_name: "no_icon",
              component_state: "active",
              component_label: "edrone",
              functionality_group: "inbox",
              functionality: "inbox",
              view: "customer_preview",
              text: "fetch_activities",
            });
            return;
          default:
            break;
        }
      },
    },
    services: {
      fetchCustomer: async (context, event) => {
        xlog({ label: "fetchCustomer", context, event });
        const response = Number(context.customerId)
          ? await axios.get("/customer-preview/get-customer-preview-by-customer-id?customer_id=" + context.customerId)
          : await axios.get("/customer-preview/get-customer-preview-by-email?email=" + context.customerId);
        return response.data.data;
      },
      fetchTags: (context, event) => async (send) => {
        xlog({ label: "fetchTags", context, event });
        try {
          const response = await axios.get("/tags");
          send({
            type: "ASSIGN_TAGS",
            data: response.data || [],
          });
        } catch (e) {
          window.showToast(window._trans._inbox.customer.notifications.getTagsError, "error");
        }
        return;
      },
      createTag: async (context, event) => {
        xlog({ label: "createTag", context, event });
        const response = await axios.put("/tags?tag_name=" + event.tag);
        return response.data?.data?.tag;
      },
      deleteCustomer: async (context, event) => {
        xlog({ label: "deleteCustomer", context, event });
        await axios.get("/customer-preview/delete-customer?customer_id=" + context.customer?.customerId);
        return null;
      },
      unsubscribeAll: async (context, event) => {
        xlog({ label: "unsubscribeAll", context, event });
        // TODO add whatsapp - update endpoints
        await axios.get(
          `/json/customer/updateEmailSubscriptionStatus?customer_id=${context.customer?.customerId}&status=false`
        );
        await axios.get(
          `/json/customer/updateSmsSubscriptionStatus?customer_id=${context.customer?.customerId}&status=false`
        );
        return null;
      },
      clearTPCID: async (context, event) => {
        xlog({ label: "clearTPCID", context, event });
        await axios.get("/customer-preview/clear-cookies?customer_id=" + context.customer?.customerId);
        return null;
      },
      exportToHTML: async (context, event) => {
        xlog({ label: "exportToHTML", context, event });
        await axios.get(
          "/json/settings/exportOnRequest?object_id=" +
            context.customer?.customerId +
            "&action_type_id=5&export_file_type_id=2"
        );
        return null;
      },
      updateCustomer: async (context, event) => {
        xlog({ label: "updateCustomer", context, event });
        const formattedBirthday = context.form?.birthday
          ? getUnix(
              new Date(
                Date.UTC(
                  context.form?.birthday.getFullYear(),
                  context.form?.birthday.getMonth(),
                  context.form?.birthday.getDate()
                )
              )
            )
          : null;

        const response = await axios.post("/customer-preview/update-customer", {
          customer_id: context.customer?.customerId,
          first_name: context.form?.firstName || null,
          last_name: context.form?.lastName || null,
          phone: context.form?.phone || null,
          language: context.form?.language,
          country: context.form?.country || null,
          city: context.form?.city || null,
          birthday: formattedBirthday,
          gender_type: context.form?.genderType || "UNKNOWN",
          email_status: context.form?.subscriptionEmailStatus || "UNKNOWN",
          sms_status: context.form?.subscriptionSMSStatus || "UNKNOWN",
          whatsapp_status: context.form?.subscriptionWhatsappStatus || "UNKNOWN",
          tags: JSON.stringify(context.form?.tags || []),
        });
        return response.data.data;
      },
      fetchActivities: async (context, event) => {
        xlog({ label: "fetchActivities", context, event });
        const dateFrom = getUnixStartOfDayPlusOneSecond(event.range[0]).toString();
        const dateTo = getUnixEndOfDay(event.range[1]).toString();
        const bitmap = event.filters?.reduce((acc, filter) => {
          return acc + Number(filter);
        }, 0);

        const response = await axios.get(
          "/customer-preview/get-activities?customer_id=" +
            context.customer?.customerId +
            "&from=" +
            dateFrom +
            "&to=" +
            dateTo +
            (event.filters?.length ? "&event_type_id=" + bitmap : "")
        );
        if (!response.data) {
          throw new Error("Error while fetching activities");
        }

        if (event.search) {
          return response.data.data.filter((activity: Activity) => {
            return JSON.stringify(activity).toLowerCase().includes(event.search.toLowerCase());
          });
        }

        return response.data.data;
      },
      activityPreviewModal: machineActivityPreview,
    },
    guards: {
      isFormChanged: (context, event) => {
        xlog({ label: "isFormChanged", type: "guard", context, event });
        return !!context.form?.changed;
      },
      isCustomerEmpty: (context, event) => {
        xlog({ label: "isCustomerEmpty", type: "guard", context, event });
        return event.data?.deleted || !event.data?.exist;
      },
      isDemo: () => {
        xlog({ label: "isDemo", type: "guard" });
        return window.EDRONE_CONTEXT.isDemo;
      },
    },
    delays: {},
  }
);

export type CustomerPreviewActor = InterpreterFrom<typeof machineCustomerPreview>;
const customerPreviewServiceSymbol: InjectionKey<CustomerPreviewActor> = Symbol();

export function provideCustomerPreviewActor(context?: Partial<Context>): {
  send: CustomerPreviewActor["send"];
  state: Ref<CustomerPreviewActor["state"]>;
} {
  // @ts-ignore
  const service = useInterpret(machineCustomerPreview, {
    devTools: true,
    context: { ...machineCustomerPreview.context, ...context },
  });
  // @ts-ignore
  provide(customerPreviewServiceSymbol, service);
  // @ts-ignore
  return useActor(service);
}

export function useCustomerPreviewActor(): {
  send: CustomerPreviewActor["send"];
  state: Ref<CustomerPreviewActor["state"]>;
} {
  const actor = inject<CustomerPreviewActor>(customerPreviewServiceSymbol);
  return useActor(actor as CustomerPreviewActor);
}
